import { useCallback, useRef } from "react";
import { serialize, uid } from "../utils/strings";

const isServer = typeof window === "undefined";

declare const window: { [key: string]: any } & Window;

const useJSONP = <Data>(
  url: string,
  options?: {
    callbackParam?: string;
    id?: string;
    timeout?: number /** milliseconds timout */;
  }
) => {
  options = {
    callbackParam: "callback",
    id: uid(),
    timeout: 30000,
    ...options,
  };
  const scriptEl = useRef<HTMLScriptElement | null>();
  const callbackId = `callback_${options.id}`;

  const removeScript = () => {
    if (scriptEl.current) {
      document.head.removeChild(scriptEl.current);
      scriptEl.current = null;
    }
  };

  return useCallback(
    (additionalQueryParams?: {
      [key: string]: string | number;
    }): Promise<Data> =>
      new Promise((resolve, reject) => {
        if (isServer) return;

        window[callbackId] = (data: Data) => {
          resolve(data);
          window[callbackId] = undefined;
          removeScript();
        };

        const script = window.document.createElement("script");
        script.src = `${url}&${options.callbackParam}=${callbackId}&${serialize(
          additionalQueryParams
        )}`;
        script.id = options.id;

        script.onerror = (e) => {
          reject(e);
          window[callbackId] = undefined;
          removeScript();
        };

        setTimeout(() => {
          reject("Request timed out");
          window[callbackId] = undefined;
          removeScript();
        }, options.timeout);

        document.head.appendChild(script);
        scriptEl.current = script;
      }),
    [url]
  );
};

export default useJSONP;
